import { Dispatch, SetStateAction, useEffect, useState } from 'react';

export const useLocalStorage = <T = string>(key: string, initialValue: T): [T, Dispatch<SetStateAction<T>>] => {
  const [storedValue, setStoredValue] = useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.error(error);

      return initialValue;
    }
  });

  const setValue: Dispatch<SetStateAction<T>> = (value) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
      window.dispatchEvent(new Event('local-storage-update'));
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    const abortController = new AbortController();
    const signal = abortController.signal;

    window.addEventListener(
      'storage',
      (event: StorageEvent) => {
        if (event.key === key) {
          try {
            setStoredValue(event.newValue ? JSON.parse(event.newValue) : initialValue);
          } catch (error) {
            console.error(error);
          }
        }
      },
      { signal }
    );

    window.addEventListener(
      'local-storage-update',
      () => {
        try {
          const item = window.localStorage.getItem(key);
          setStoredValue(item ? JSON.parse(item) : initialValue);
        } catch (error) {
          console.error(error);
        }
      },
      { signal }
    );

    return () => {
      abortController.abort();
    };
  }, [key, initialValue]);

  return [storedValue, setValue];
};
